import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const HRCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/hr`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching HR capabilities:', error);
      showNotification?.('Failed to load HR capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/hr`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('HR capabilities saved successfully', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving HR capabilities:', error);
      showNotification?.('Failed to save HR capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!capabilities) {
    return <div className="p-4 text-gray-500">No HR capabilities configured</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Enable Toggle */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={capabilities.enabled || false}
            onChange={(e) => updateCapability('enabled', e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">
            Enable HR Capabilities
          </span>
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Enable this agent to handle HR-related tasks including recruitment, onboarding, performance management, and leave management.
        </p>
      </div>

      {capabilities.enabled && (
        <>
          {/* Recruitment Capabilities */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Recruitment</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.recruitment?.enabled || false}
                  onChange={(e) => updateCapability('recruitment.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable recruitment capabilities</span>
              </label>

              {capabilities.recruitment?.enabled && (
                <>
                  {/* Job Posting */}
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Job Posting</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.recruitment?.jobPosting?.enabled || false}
                        onChange={(e) => updateCapability('recruitment.jobPosting.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable job posting</span>
                    </label>

                    {capabilities.recruitment?.jobPosting?.enabled && (
                      <>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={capabilities.recruitment?.jobPosting?.requireApproval || false}
                            onChange={(e) => updateCapability('recruitment.jobPosting.requireApproval', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="ml-2 text-sm">Require approval before posting</span>
                        </label>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={capabilities.recruitment?.jobPosting?.autoPublish || false}
                            onChange={(e) => updateCapability('recruitment.jobPosting.autoPublish', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="ml-2 text-sm">Auto-publish to job boards</span>
                        </label>
                      </>
                    )}
                  </div>

                  {/* Candidate Screening */}
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Candidate Screening</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.recruitment?.candidateScreening?.enabled || false}
                        onChange={(e) => updateCapability('recruitment.candidateScreening.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable candidate screening</span>
                    </label>

                    {capabilities.recruitment?.candidateScreening?.enabled && (
                      <>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={capabilities.recruitment?.candidateScreening?.autoScreen || false}
                            onChange={(e) => updateCapability('recruitment.candidateScreening.autoScreen', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="ml-2 text-sm">Auto-screen candidates</span>
                        </label>

                        <div>
                          <label className="block text-sm font-medium mb-1">Screening Threshold (%)</label>
                          <input
                            type="number"
                            value={capabilities.recruitment?.candidateScreening?.screeningThreshold || 70}
                            onChange={(e) => updateCapability('recruitment.candidateScreening.screeningThreshold', Number(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                            min="0"
                            max="100"
                          />
                          <p className="text-xs text-gray-500 mt-1">Minimum score to pass screening</p>
                        </div>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={capabilities.recruitment?.candidateScreening?.autoReject || false}
                            onChange={(e) => updateCapability('recruitment.candidateScreening.autoReject', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="ml-2 text-sm">Auto-reject low-scoring candidates</span>
                        </label>

                        {capabilities.recruitment?.candidateScreening?.autoReject && (
                          <div>
                            <label className="block text-sm font-medium mb-1">Auto-reject Threshold (%)</label>
                            <input
                              type="number"
                              value={capabilities.recruitment?.candidateScreening?.autoRejectThreshold || 30}
                              onChange={(e) => updateCapability('recruitment.candidateScreening.autoRejectThreshold', Number(e.target.value))}
                              className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                              min="0"
                              max="100"
                            />
                            <p className="text-xs text-gray-500 mt-1">Candidates below this score will be auto-rejected</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Phone Screening */}
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Phone Screening</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.recruitment?.phoneScreening?.enabled || false}
                        onChange={(e) => updateCapability('recruitment.phoneScreening.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable phone screening</span>
                    </label>

                    {capabilities.recruitment?.phoneScreening?.enabled && (
                      <>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={capabilities.recruitment?.phoneScreening?.autoSchedule || false}
                            onChange={(e) => updateCapability('recruitment.phoneScreening.autoSchedule', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="ml-2 text-sm">Auto-schedule phone screens</span>
                        </label>

                        <div>
                          <label className="block text-sm font-medium mb-1">Max Duration (minutes)</label>
                          <input
                            type="number"
                            value={capabilities.recruitment?.phoneScreening?.maxDuration || 15}
                            onChange={(e) => updateCapability('recruitment.phoneScreening.maxDuration', Number(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                            min="5"
                            max="60"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">Passing Score (%)</label>
                          <input
                            type="number"
                            value={capabilities.recruitment?.phoneScreening?.passingScore || 70}
                            onChange={(e) => updateCapability('recruitment.phoneScreening.passingScore', Number(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                            min="0"
                            max="100"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Interview Scheduling */}
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Interview Scheduling</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.recruitment?.interviewScheduling?.enabled || false}
                        onChange={(e) => updateCapability('recruitment.interviewScheduling.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable interview scheduling</span>
                    </label>

                    {capabilities.recruitment?.interviewScheduling?.enabled && (
                      <>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={capabilities.recruitment?.interviewScheduling?.autoSchedule || false}
                            onChange={(e) => updateCapability('recruitment.interviewScheduling.autoSchedule', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="ml-2 text-sm">Auto-schedule interviews</span>
                        </label>

                        <div>
                          <label className="block text-sm font-medium mb-1">Default Interview Duration (minutes)</label>
                          <input
                            type="number"
                            value={capabilities.recruitment?.interviewScheduling?.defaultInterviewDuration || 60}
                            onChange={(e) => updateCapability('recruitment.interviewScheduling.defaultInterviewDuration', Number(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                            min="15"
                            max="240"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">Buffer Time Between Interviews (minutes)</label>
                          <input
                            type="number"
                            value={capabilities.recruitment?.interviewScheduling?.bufferTime || 15}
                            onChange={(e) => updateCapability('recruitment.interviewScheduling.bufferTime', Number(e.target.value))}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                            min="0"
                            max="60"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Offer Management */}
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Offer Management</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.recruitment?.offerManagement?.enabled || false}
                        onChange={(e) => updateCapability('recruitment.offerManagement.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable offer management</span>
                    </label>

                    {capabilities.recruitment?.offerManagement?.enabled && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={capabilities.recruitment?.offerManagement?.requireApproval || false}
                          onChange={(e) => updateCapability('recruitment.offerManagement.requireApproval', e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">Require approval before sending offers</span>
                      </label>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Onboarding Capabilities */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Onboarding</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.onboarding?.enabled || false}
                  onChange={(e) => updateCapability('onboarding.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable onboarding capabilities</span>
              </label>

              {capabilities.onboarding?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.onboarding?.autoInitiate || false}
                      onChange={(e) => updateCapability('onboarding.autoInitiate', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-initiate onboarding process</span>
                  </label>

                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Document Collection</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.onboarding?.documentCollection?.enabled || false}
                        onChange={(e) => updateCapability('onboarding.documentCollection.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable document collection</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Performance Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Performance Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.performanceManagement?.enabled || false}
                  onChange={(e) => updateCapability('performanceManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable performance management</span>
              </label>

              {capabilities.performanceManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.performanceManagement?.goalTracking || false}
                      onChange={(e) => updateCapability('performanceManagement.goalTracking', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable goal tracking</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.performanceManagement?.feedbackCollection || false}
                      onChange={(e) => updateCapability('performanceManagement.feedbackCollection', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable feedback collection</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Leave Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Leave Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.leaveManagement?.enabled || false}
                  onChange={(e) => updateCapability('leaveManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable leave management</span>
              </label>

              {capabilities.leaveManagement?.enabled && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={capabilities.leaveManagement?.autoApprove || false}
                    onChange={(e) => updateCapability('leaveManagement.autoApprove', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="ml-2 text-sm">Auto-approve leave requests</span>
                </label>
              )}
            </div>
          </div>

          {/* Policy and Compliance */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Policy and Compliance</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.policyCompliance?.enabled || false}
                  onChange={(e) => updateCapability('policyCompliance.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable policy and compliance checks</span>
              </label>

              {capabilities.policyCompliance?.enabled && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={capabilities.policyCompliance?.complianceChecks || false}
                    onChange={(e) => updateCapability('policyCompliance.complianceChecks', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="ml-2 text-sm">Enable automated compliance checks</span>
                </label>
              )}
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-[#1a1a1a] p-4 border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default HRCapabilities;
